'use client'

import { useEffect, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgChevronDown from '@/icons/regular/ChevronDown'
import type { FilterOption } from '@/lib/directory/build-filter-options'

import { ChoiceCheck } from './choice-check'
import { ChoicePill } from './choice-pill'

interface ChoiceGroupProps {
  id: string
  title: string
  options: FilterOption[]
  selected: string[]
  mode: 'pills' | 'checks'
  onToggle: (value: string) => void
  /** Closed by default for the long-tail groups; a group with an active value opens itself. */
  defaultOpen?: boolean
  /** Show this many, then a "Show all" toggle. */
  initialCount?: number
  headerControl?: React.ReactNode
}

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-')

// One facet: a native <details> with the title (and a count badge when any
// value is active), then pills or checkbox rows, then an optional expander.
export const ChoiceGroup = ({
  id,
  title,
  options,
  selected,
  mode,
  onToggle,
  defaultOpen = true,
  initialCount,
  headerControl,
}: ChoiceGroupProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(defaultOpen)
  const [expanded, setExpanded] = useState(false)
  const activeCount = options.filter((option) => selected.includes(option.value)).length

  // A value activated while the group is closed (deep link, cleared + re-added)
  // reveals the group; a user closing it afterwards is respected.
  useEffect(() => {
    if (activeCount > 0) {
      setOpen(true)
    }
  }, [activeCount])

  if (options.length === 0) {
    return null
  }

  const hasMore = initialCount != null && options.length > initialCount
  const visible =
    hasMore && !expanded
      ? options.filter((option, index) => index < initialCount || selected.includes(option.value))
      : options

  return (
    <div className="relative">
      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className="group border-navy/10 border-b">
        <summary className="text-navy flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-3 py-2 text-[15px] font-semibold [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">
            {title}
            {activeCount > 0 ? (
              <span className="bg-lapis rounded-full px-1.5 py-0.5 text-[11px] leading-none font-bold text-white">
                {activeCount}
              </span>
            ) : null}
          </span>
          {headerControl ? <span aria-hidden className="min-w-20 flex-1" /> : null}
          <SvgChevronDown
            aria-hidden
            className="h-3 w-3 shrink-0 fill-current opacity-60 transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="pb-4">
          {mode === 'pills' ? (
            <div className="flex flex-wrap gap-2">
              {visible.map((option) => (
                <ChoicePill
                  key={option.value}
                  option={option}
                  selected={selected.includes(option.value)}
                  onToggle={onToggle}
                />
              ))}
            </div>
          ) : (
            <ul className="flex flex-col">
              {visible.map((option) => (
                <li key={option.value}>
                  <ChoiceCheck
                    id={`${id}-${slugify(option.value)}`}
                    option={option}
                    selected={selected.includes(option.value)}
                    onToggle={onToggle}
                  />
                </li>
              ))}
            </ul>
          )}
          {hasMore ? (
            <button
              type="button"
              data-tracking="ignore"
              onClick={() => setExpanded((v) => !v)}
              className="text-lapis hover:text-navy mt-2 cursor-pointer text-[14px] font-medium underline decoration-dotted underline-offset-4"
            >
              {expanded
                ? t('directory.showLess', 'Show fewer')
                : t('directory.showAll', 'Show all {{count}}', { count: options.length })}
            </button>
          ) : null}
        </div>
      </details>
      {headerControl ? <div className="absolute top-3 right-5">{headerControl}</div> : null}
    </div>
  )
}
